import type { RunStatus } from './index'

export const EXECUTION_STATUSES = [
    'running',
    'queued',
    'waiting',
    /** A person paused it (#809): what had not started is held until someone resumes or cancels it. */
    'paused',
    'blocked',
    'succeeded',
    'failed',
    'cancelled',
    'skipped',
] as const
export type ExecutionStatus = (typeof EXECUTION_STATUSES)[number]

/**
 * What a stop records as the outcome of a succeeded run that named none, on a graph that branches on outcomes
 * (`successor.dead_end`, and a stop reason of `outcome_not_declared:(no outcome)`). Shared so the portal can say so in
 * words rather than print the marker.
 */
export const NO_OUTCOME_NAMED = '(no outcome)'

/** The `/executions` board columns an execution can be in, in display order. */
export const EXECUTION_LANES = ['ready', 'blocked', 'inProgress', 'complete'] as const
export type ExecutionLane = (typeof EXECUTION_LANES)[number]

/**
 * Every column of the board (#1830): the work list's Backlog ahead of the execution lanes. Only a work item no run
 * has claimed is in Backlog; an execution is never there.
 */
export const BOARD_LANES = ['backlog', ...EXECUTION_LANES] as const
export type BoardLane = (typeof BOARD_LANES)[number]

/**
 * The board lane of one run standing for a piece of work on its own (#1830: a work item held or worked by a run
 * that is on no execution): where its status would put an execution of that one run. A failure nobody has cleared
 * is Blocked, as it is on Blockers; a cleared one is Complete.
 */
export function runLane(status: RunStatus, cleared = false): ExecutionLane {
    switch (status) {
        case 'queued':
        case 'pending':
            return 'ready'
        case 'running':
            return 'inProgress'
        case 'waiting_on_gate':
        case 'waiting_on_run':
        case 'paused_spend':
        case 'paused_plan':
        case 'paused_team':
        case 'paused':
        case 'blocked':
            return 'blocked'
        case 'failed':
            return cleared ? 'complete' : 'blocked'
        default:
            return 'complete'
    }
}

/**
 * Where each execution status sits on the board while none of its runs is on the Blockers page. Queued work is
 * Ready and running work is In Progress. Waiting — on a gate or approval, another run, or a spend allowance — is
 * Blocked, because nothing moves until someone acts. Every other status is where the execution ended up, so it
 * is Complete; the status pill on the card still says which (failed, stopped, cancelled, …).
 *
 * The Blocked lane also holds any execution one of whose runs the Blockers page lists (an uncleared failure, a
 * blocked run, or a stopped pipeline): see `executionLane`. So a failed execution is Blocked exactly while its
 * failure is on Blockers, and moves to Complete once someone clears it, retries past it, or newer work on its
 * pipeline takes over (#557). A `Record` so a new status fails to compile until someone places it.
 */
export const EXECUTION_LANE_BY_STATUS: Record<ExecutionStatus, ExecutionLane> = {
    queued: 'ready',
    running: 'inProgress',
    waiting: 'blocked',
    // A pause waits on the person who can resume it, like a gate waits on its decision.
    paused: 'blocked',
    blocked: 'complete',
    failed: 'complete',
    succeeded: 'complete',
    cancelled: 'complete',
    skipped: 'complete',
}

/**
 * The board lane for an execution: its status's lane, except that one with a run on the Blockers page
 * (`onBlockers`) is Blocked rather than Complete. Queued and running work stay in Ready and In Progress whatever
 * else is true of them, since someone is already on it.
 */
export function executionLane(status: ExecutionStatus, onBlockers: boolean): ExecutionLane {
    const lane = EXECUTION_LANE_BY_STATUS[status]
    return onBlockers && lane === 'complete' ? 'blocked' : lane
}

/**
 * Why an execution cannot be closed right now, in words a console can show. The API refuses a close with one of
 * these as its `message`, so a console can recognise the refusal and show it as it stands.
 */
export const EXECUTION_CLOSE_REFUSALS = {
    execution_not_finished: 'This execution still has work queued or running. Close it once it has finished.',
    execution_waiting:
        'This execution is waiting on a gate, another run or a spend allowance. Close it once it has finished.',
    execution_paused: 'This execution is paused. Resume or cancel it, then close it once it has finished.',
    execution_on_blockers:
        'One of its runs is on the Blockers page. Clear or retry it there, then close the execution.',
} as const

export type ExecutionCloseRefusal = {
    error: keyof typeof EXECUTION_CLOSE_REFUSALS
    message: (typeof EXECUTION_CLOSE_REFUSALS)[keyof typeof EXECUTION_CLOSE_REFUSALS]
}

/**
 * Closing takes a finished execution off the board (#557), so only one in the Complete lane can be closed, or one
 * on the Waiting list (#1457), where closing is how someone says the work is done: nothing may hide work that is
 * queued, running, waiting, or still listed on Blockers. The API refuses with this reason and a console explains
 * the same way, so the two never word it differently.
 */
export function executionCloseRefusal(
    execution: Pick<ExecutionSummary, 'lane' | 'status' | 'closed'> & Partial<Pick<ExecutionSummary, 'onWaiting'>>,
): ExecutionCloseRefusal | null {
    if (execution.closed || execution.lane === 'complete' || execution.onWaiting) {
        return null
    }
    let error: ExecutionCloseRefusal['error'] = 'execution_on_blockers'
    if (execution.status === 'paused') {
        // A pause sits in Blocked because a person holds it, not because Blockers lists it: say which.
        error = 'execution_paused'
    } else if (execution.lane === 'ready' || execution.lane === 'inProgress') {
        error = 'execution_not_finished'
    } else if (execution.status === 'waiting') {
        error = 'execution_waiting'
    }
    return { error, message: EXECUTION_CLOSE_REFUSALS[error] }
}

/**
 * Which execution statuses still have work a cancel can stop, so a console can offer the action (#348).
 * Every value here is one `executionStatus()` (`apps/api/src/runs/execution-history.ts`) actually emits:
 * `queued` is what it reports for an execution whose runs are all still `pending` or `queued` — nothing
 * started yet — so a not-yet-started execution is cancellable too. `waiting` covers `waiting_on_gate`,
 * `waiting_on_run` and `paused_spend`. The terminal four cannot be cancelled: the API treats a cancel with
 * nothing open as a no-op rather than relabelling a finished execution. A `Record` rather than a list so a
 * new `ExecutionStatus` fails to compile until someone says which side of this line it falls on.
 */
export const EXECUTION_OPEN_BY_STATUS: Record<ExecutionStatus, boolean> = {
    queued: true,
    running: true,
    waiting: true,
    paused: true,
    blocked: true,
    succeeded: false,
    failed: false,
    cancelled: false,
    skipped: false,
}

/** True while an execution still has runs a cancel would stop. */
export function executionIsOpen(status: ExecutionStatus): boolean {
    return EXECUTION_OPEN_BY_STATUS[status]
}

/**
 * #431: where an item another team's run added came from: the sending team (its name as it was when it sent) and
 * the run that sent it. An item added the ordinary way has none.
 */
export type WorkItemOrigin = { teamId: string; teamName: string; runId: string }

/**
 * One work item as the board shows it (#1830). It is one card for its whole life: on its own while no execution
 * holds it (unclaimed, in Backlog, in its list's order; or held or worked by a run that is on no execution, in that
 * run's lane), and part of its execution's card once the run holding it is on one (`ExecutionSummary.workItems`).
 * An item closed without a run ever holding it is on no lane: the board shows work, and it was never worked.
 */
export type WorkItemCard = {
    id: string
    teamId: string
    team: ExecutionLabel | null
    title: string
    kind: string
    state: 'proposed' | 'ready' | 'held' | 'in_progress' | 'done' | 'dropped'
    priority: 'urgent' | 'high' | 'medium' | 'low' | null
    position: number
    /** The item it sits under on its list (one level only), or null: the Backlog groups children under it. */
    parentId: string | null
    /** True while a person has yet to approve it (`proposed`): the card says so. */
    proposed: boolean
    /** The run holding it, or the run that last held it once it is closed, with its status; null while unclaimed. */
    run: { id: string; status: RunStatus } | null
    /** The execution that run is on, or null: a card with none stands alone on the board. */
    executionId: string | null
    lane: BoardLane
    createdAt: string
    updatedAt: string
    closedAt: string | null
    /** #431: the team and run that sent it, for an item another team's run added; absent or null otherwise. */
    origin?: WorkItemOrigin | null
}

/** `GET /v1/executions/board`: every lane from one read. Closed executions are on none of them. */
export type ExecutionBoard = {
    lanes: Record<ExecutionLane, ExecutionSummary[]>
    /** True count per lane. `lanes.complete` is capped to the newest entries, so it can be shorter than `totals.complete`. */
    totals: Record<ExecutionLane, number>
    /**
     * The work items standing on the board on their own (#1830), by lane: Backlog holds the unclaimed ones in the
     * list's order; a run's lane holds the ones a run on no execution holds or worked. An item on an execution is
     * on that execution's card instead. `items.complete` is capped like `lanes.complete`.
     */
    items: Record<BoardLane, WorkItemCard[]>
    /** True count of items per lane. */
    itemTotals: Record<BoardLane, number>
    /**
     * The Backlog's parents on the board, by item id: how many of each one's children stand in each lane, counted over
     * the whole board (the ones on an execution in that execution's lane), not only the ones the Backlog shows.
     */
    rollups: Record<string, Record<BoardLane, number>>
    /**
     * Set when the board is read with `laneLimit` (`BOARD_LANE_LIMIT_PARAM`): the page each lane shows, of how many.
     * A lane's page is `laneLimit` entries: its standalone items, then its executions; in Backlog an entry is a parent
     * with its children in the Backlog, or an item under no parent, the parents first. Read without it, every lane
     * is whole (Complete capped as above), so a reader that needs every execution keeps them all.
     */
    pages?: Record<BoardLane, { page: number; pages: number }>
}

/** The board query parameter that pages every lane, and the most entries one lane page may hold. */
export const BOARD_LANE_LIMIT_PARAM = 'laneLimit'
export const BOARD_LANE_LIMIT_MAX = 100

/** The board query parameter that names one lane's page (`backlogPage`, `inProgressPage`, …), from 1. */
export function boardLanePageParam(lane: BoardLane): string {
    return `${lane}Page`
}

/** `POST /v1/executions/close-completed`: how many executions the sweep closed. */
export type ExecutionCloseCompletedResult = { closed: number }

/** Enterprise-wide activity, independent of execution/run table filters and pagination. */
export type ExecutionCounts = {
    running: number
    waiting: number
    queued: number
    /** Executions that cannot go on until someone looks: a run blocked that will not resume by itself. */
    blocked: number
    /** Of `waiting`, the executions waiting on a person's decision at a gate. */
    waitingOnGate: number
    /** Individual running task runs, including OS tasks excluded from business executions. */
    runningRuns: number
    /** Runs waiting on a gate, another run, or a spend allowance. */
    waitingRuns: number
    /** Of `waitingRuns`, the runs waiting on a person's decision at a gate. */
    waitingRunsOnGate: number
    /** Runs blocked that will not resume by themselves. */
    blockedRuns: number
}

export type ExecutionLabel = { id: string; name: string }
export type ExecutionMember = ExecutionLabel & { avatarUrl: string | null }
export type ExecutionRole = ExecutionLabel & { slug: string }
export type ExecutionTaskRef = { id: string | null; slug: string | null; name: string }

/** A skill-provided URL on the execution (`os.add_execution_link`). */
export type ExecutionResource = { name: string; url: string }

export type ExecutionSummary = {
    /** The canonical root run. Any run ID in the execution also resolves its detail. */
    id: string
    rootRunId: string
    /** Last `os.set_execution_name`, or the starting task name when none was set. */
    name: string
    /** Deduped by URL; later calls with the same URL update the label. */
    resources: ExecutionResource[]
    startingTask: ExecutionTaskRef
    status: ExecutionStatus
    lineage: 'recorded' | 'standalone'
    /**
     * While `status` is `paused` (#809): who paused the execution and when. Null once it is resumed, or once the
     * pause holds nothing any more. The API always sends both; optional only for hand-built summaries.
     */
    pausedBy?: ExecutionLabel | null
    pausedAt?: string | null
    stoppedReason: string | null
    /**
     * While `status` is `waiting`, the run status it waits on: `waiting_on_gate` for a decision, `paused_team` for a
     * switched-off team, and so on. Null otherwise.
     */
    waitingOn: RunStatus | null
    /** The run whose end stopped the chain (`stoppedReason`): nothing ran after it. Null when nothing stopped it. */
    stoppedRunId: string | null
    runCount: number
    taskCount: number
    activeRunIds: string[]
    currentTasks: ExecutionTaskRef[]
    startedAt: string
    updatedAt: string
    finishedAt: string | null
    costUsdCents: number
    teams: ExecutionLabel[]
    members: ExecutionMember[]
    roles: ExecutionRole[]
    /** Who cancelled the execution, and when: set once a whole-execution cancel completes. */
    cancelledBy: ExecutionLabel | null
    cancelledAt: string | null
    /** Why a task stopped the execution (`os.stop_execution`); absent for an operator cancel. */
    cancelledReason?: string | null
    /**
     * True while one of its runs is listed on the Blockers page: an uncleared failure, a blocked run, or a
     * stopped pipeline. The same rules decide both, so the board's Blocked lane and Blockers agree (#557).
     */
    onBlockers: boolean
    /**
     * True while its work is on the Waiting list (#1457): it ended without finishing and nothing is working on it,
     * so it sits in Blocked until someone restarts it or says it is done. Closing it is that finish.
     */
    onWaiting: boolean
    /** Its board lane: `executionLane(status, onBlockers || onWaiting)`. */
    lane: ExecutionLane
    /**
     * True while a close is in effect: the execution is off the board and out of its totals, still reachable by
     * id or any run id and listed under `GET /v1/executions?closed=true`. A reopen ends it, and so does new work
     * joining the execution, which puts it back in its lane (#557).
     */
    closed: boolean
    /** The latest close: who and when. Kept after a reopen, so the record of who closed it is never lost. */
    closedBy: ExecutionLabel | null
    closedAt: string | null
    /** The latest reopen, if any. */
    reopenedBy: ExecutionLabel | null
    reopenedAt: string | null
    /** The person who asked for the work, with their display name, or null when nobody did. */
    requestedBy?: ExecutionLabel | null
    /** The work items a run of this execution holds or worked (#1830): the execution's card is theirs too. */
    workItems?: WorkItemCard[]
}

export type ExecutionRun = {
    id: string
    taskId: string
    taskVersionId: string
    task: ExecutionTaskRef
    teamId: string | null
    memberId: string | null
    team: ExecutionLabel | null
    member: ExecutionMember | null
    role: ExecutionRole | null
    status: RunStatus
    createdAt: string
    startedAt: string | null
    finishedAt: string | null
    updatedAt: string
    notBefore: string | null
    blockedReason: string | null
    /**
     * A blocked run the OS is about to retry by itself (a service it relies on failed, inside the automatic retry
     * cycle): nobody needs to act, and its execution reads as queued rather than blocked.
     */
    resumesByItself: boolean
    costUsdCents: number
    /**
     * Why a `succeeded` run still stopped its pipeline (`outcome_not_declared:<outcome>`: its task has no next step for
     * the outcome it ended on), or null. Sent by the execution detail read; its status stays `succeeded`.
     */
    stoppedReason?: string | null
}

export type ExecutionLink = {
    sourceRunId: string
    targetRunId: string
    kind: 'successor' | 'retry'
    /** Empty when historic events do not identify one unambiguous configured route. */
    edgeIds: string[]
}

export type ExecutionTaskNode = {
    id: string
    taskId: string | null
    slug: string
    name: string
    goal: string
    team: ExecutionLabel | null
    member: ExecutionMember | null
    role: ExecutionRole | null
    missing: boolean
    versionIds: string[]
}

export type ExecutionTaskEdge = {
    id: string
    source: string
    target: string
    on: string
    when: string | null
    versionIds: string[]
}

/**
 * Where the work of an execution that stopped (`stoppedRunId`) went on: the newest run another execution started on
 * the same issue or pull request after the stop. Its execution opens from `runId`.
 */
export type ExecutionContinuation = {
    runId: string
    task: ExecutionTaskRef
    status: RunStatus
    createdAt: string
}

/** One pause or resume of a whole execution (#809): which it was, who did it and when, from its own event. */
export type ExecutionPauseEntry = { action: 'paused' | 'resumed'; by: ExecutionLabel | null; at: string }

export type ExecutionDetail = ExecutionSummary & {
    runs: ExecutionRun[]
    links: ExecutionLink[]
    graph: { nodes: ExecutionTaskNode[]; edges: ExecutionTaskEdge[] }
    /**
     * Every pause and resume recorded on the execution (`execution.paused` / `execution.resumed`), oldest first, so
     * its history still shows them once it is resumed. The API always sends it; optional only for hand-built details.
     */
    pauseHistory?: ExecutionPauseEntry[]
    /** Null unless this execution stopped and later work on its issue or pull request is in another execution. */
    continuedIn: ExecutionContinuation | null
}
